import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { CenteredPage } from '../app/CenteredPage';
import { Button, ButtonLink } from '../components/Button';
import { Icon } from '../components/Icon';
import { MapMarker } from '../components/illustrations';
import { Paper } from '../components/Paper';
import { ErrorNotice, Loading } from '../components/States';
import { Turnstile, turnstileEnabled } from '../components/Turnstile';
import { meQueryKey, useMe } from '../features/auth/session';
import { DevLocationPicker } from '../features/location/DevLocationPicker';
import { GEO_MESSAGES, GeoError, readPositionOnce } from '../features/location/geolocation';
import { verifyLocation } from '../lib/api';
import { shortDate } from '../lib/time';
import styles from './Onboarding.module.css';
import { FEED_REACH } from '../../shared/region';

interface Props {
  /** "onboarding" is the full-screen first-run step; "change" lives inside the app shell. */
  mode: 'onboarding' | 'change';
}

export function VerifyLocationPage({ mode }: Props) {
  const { data: me, isPending } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [verifiedPlace, setVerifiedPlace] = useState<string | null>(null);
  const [geoMessage, setGeoMessage] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: async () => {
      setGeoMessage(null);
      const position = await readPositionOnce();
      return verifyLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        turnstileToken: captcha ?? '',
      });
    },
    onSuccess: async (result) => {
      // Show the success screen before the refreshed profile arrives.
      if (result.ok) setVerifiedPlace(result.placeName);
      await queryClient.invalidateQueries();
      await queryClient.refetchQueries({ queryKey: meQueryKey });
    },
    onError: (error) => {
      if (error instanceof GeoError) setGeoMessage(GEO_MESSAGES[error.reason]);
      setCaptchaKey((k) => k + 1); // Turnstile tokens are single-use.
    },
  });

  if (isPending) return <Loading />;
  if (!me) return <Navigate to="/welcome" replace />;
  if (mode === 'onboarding' && !me.displayName) return <Navigate to="/onboarding/name" replace />;
  if (mode === 'onboarding' && me.location && !verifiedPlace) return <Navigate to="/" replace />;

  const cooldownUntil =
    mode === 'change' && me.location && new Date(me.location.nextChangeAt) > new Date()
      ? me.location.nextChangeAt
      : null;

  const Wrapper = mode === 'onboarding' ? CenteredPage : Section;

  if (verifiedPlace) {
    return (
      <Wrapper>
        <Paper className={styles.success}>
          <div className={styles.art}>
            <MapMarker />
          </div>
          <h1 className={styles.title}>You're a verified local in {verifiedPlace}</h1>
          <p className={styles.lede}>Your board shows notes from neighbors within {FEED_REACH} of home.</p>
          <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
            Open my board
          </Button>
        </Paper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <header className={styles.header}>
        <div className={styles.art}>
          <MapMarker />
        </div>
        {mode === 'onboarding' && <p className={styles.steps}>Step 2 of 2</p>}
        <h1 className={styles.title}>{mode === 'onboarding' ? 'Confirm you live here' : 'Move your home area'}</h1>
        <p className={styles.lede}>
          Roadrunner shows you notes from people who live near you, so we check your location once.
        </p>
      </header>

      <Paper className={styles.form}>
        {me.location && mode === 'change' && (
          <p className={styles.status}>
            Your home area is <strong>{me.location.placeName}</strong>, verified {shortDate(me.location.verifiedAt)}.
          </p>
        )}
        <ul className={styles.facts}>
          <li>
            <Icon name="phone" />
            <span>
              <strong>Use your phone.</strong> Computers often can't tell where they are, especially on satellite
              internet like Starlink.
            </span>
          </li>
          <li>
            <Icon name="home" />
            <span>
              <strong>Do this from home.</strong> Your board is built around the spot where you verify.
            </span>
          </li>
          <li>
            <Icon name="pin" />
            <span>
              We turn your location into a rough area, about a city block, and don't keep the exact coordinates.
            </span>
          </li>
          <li>
            <Icon name="person" />
            <span>Neighbors see “Verified local” and your town, like “Catalina.” Never your street or your house.</span>
          </li>
          <li>
            <Icon name="refresh" />
            <span>You can move your home area once every 30 days.</span>
          </li>
        </ul>

        {cooldownUntil ? (
          <p className={styles.status}>You can move your home area again on {shortDate(cooldownUntil)}.</p>
        ) : (
          <>
            {turnstileEnabled && <Turnstile onToken={setCaptcha} resetKey={captchaKey} />}
            {geoMessage && (
              <p role="alert" className={styles.status}>
                {geoMessage}
              </p>
            )}
            {verify.error && !(verify.error instanceof GeoError) && <ErrorNotice error={verify.error} />}
            <Button
              variant="primary"
              block
              onClick={() => verify.mutate()}
              disabled={verify.isPending || (turnstileEnabled && !captcha)}
            >
              <Icon name="pin" size={18} />
              {verify.isPending ? 'Checking your location…' : 'Verify my location'}
            </Button>
          </>
        )}

        {mode === 'onboarding' && me.role !== 'member' && (
          <ButtonLink to="/mod" variant="quiet" block>
            Skip for now (moderators)
          </ButtonLink>
        )}
      </Paper>

      {import.meta.env.DEV && <DevLocationPicker onDone={setVerifiedPlace} />}
    </Wrapper>
  );
}

function Section({ children }: { children: ReactNode }) {
  return <div style={{ display: 'grid', gap: 'var(--s5)' }}>{children}</div>;
}
